// Package imports:
import React from "react";
import Bugsnag from "@bugsnag/js";
import { Form, Formik } from "formik";
import * as Yup from 'yup';
// Component imports:
import Button from "../../../ui-elements/Button/Button";
import Input from "../../../ui-elements/Input/Formik/Input";
import Link from "../../../ui-elements/Link/Link";
import Checkbox from "../../../ui-elements/Input/Formik/Checkbox";
import ExampleReportButton from "../../ExampleReportButton/ExampleReportButton";
// Service imports:
import { GET_KELDAN_API_URL } from "../../../services/config";
import { useBuyingProcess } from "../../../services/buyhook";
import { ErrorMessages } from "../../../services/errorMessages";
// Type imports:
import { IKeldanApiContact } from "../../../types/KeldanTypes";

interface IProps {
    bondsAllowed: boolean
}

const VehicleSearch: React.FC<IProps> = ({
    bondsAllowed
}) => {
    const { tryPurchaseItem, tryToBuyItem, resetState, ModalsAndToasts } = useBuyingProcess(async (values: IFormValues) => {
        try {
            const requestQuery = new URLSearchParams({
                permanent: values.fastanr,
                registration: values.skraningarnr,
                vin: values.verksmidjunr,
                reference: values.tilvisun,
                bonds: values.bonds.toString()
            });
            const url = `${GET_KELDAN_API_URL()}/Vehicle/VehicleSearch`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                } else {
                    const responseBody: IKeldanApiContact = await response.json();
                    const { message } = responseBody;
                    
                    if (message === ErrorMessages.NoCard) {
                        resetState('no card');
                    } else if (message) {
                        resetState({
                            type: 'alert',
                            headText: message
                        });
                    } else {
                        resetState('bad request')
                    }
                }
            } else {
                resetState('response not ok')
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            resetState('network error')
        }
    }, 'ökutækjaskrá');

    return (
        <div className="Okutaekjaskra">
            <div className="section__body">
                <div className='KCL_vehicle-search-form'>
                    <h3>Uppfletting í Ökutækjaskrá</h3>
                    <div className='search-form-description'>
                        <span>Með því að slá inn fasta-, skráningar- eða verksmiðjunúmer er hægt að kaupa upplýsingar um ökutæki. <Link linkSize="18" href="/Verdskra">Sjá verðskrá.</Link></span>
                    </div>
                    <Formik
                        initialValues={{
                            fastanr: '',
                            skraningarnr: '',
                            verksmidjunr: '',
                            tilvisun: '',
                            bonds: false
                        }}
                        validationSchema={Yup.object({
                            fastanr: Yup.string(),
                            skraningarnr: Yup.string(),
                            verksmidjunr: Yup.string(),
                            tilvisun: Yup.string(),
                            bonds: Yup.boolean()
                        })}
                        onSubmit={(values) => tryPurchaseItem(values)}
                        component={(props: { values: IFormValues }) => (
                            <SubFormComponent
                                bondsAllowed={bondsAllowed}
                                values={props.values}
                                loading={(tryToBuyItem !== null)}
                            />
                        )}
                    />
                    <ExampleReportButton size='lg' reportType='vehicle' />
                </div>
            </div>
            <ModalsAndToasts />
        </div>
    );
}

export default VehicleSearch;

interface IFormValues {
    fastanr: string,
    skraningarnr: string,
    verksmidjunr: string,
    tilvisun: string,
    bonds: boolean
}

interface ISubFormProps {
    bondsAllowed: boolean,
    values: IFormValues,
    loading: boolean,
}

const SubFormComponent: React.FC<ISubFormProps> = ({
    bondsAllowed,
    values,
    loading,
}) => {
    return (
        <Form>
            <div className="form__body">
                <div className="form__section">
                    <div className="form__row">
                        <div className="form__col">
                            <Input
                                className='inputFastanr'
                                label="Fastanúmer"
                                name="fastanr"
                                id="fastanr"
                                value={values.fastanr}
                                disabled={(values.skraningarnr !== '' || values.verksmidjunr !== '')}
                            />
                        </div>
                        <div className="form__col">
                            <Input
                                className='inputSkraningarnr'
                                label="Skráningarnúmer"
                                name="skraningarnr"
                                id="skraningarnr"
                                value={values.skraningarnr}
                                disabled={(values.fastanr !== '' || values.verksmidjunr !== '')}
                            />
                        </div>
                        <div className="form__col">
                            <Input
                                className='inputVerksmidjunr'
                                label="Verksmiðjunúmer"
                                name="verksmidjunr"
                                id="verksmidjunr"
                                value={values.verksmidjunr}
                                disabled={(values.fastanr !== '' || values.skraningarnr !== '')}
                            />
                        </div>
                        <div className="form__col inputTilvisun">
                            <Input
                                label="Tilvísun á reikning"
                                name="tilvisun"
                                id="tilvisun"
                                value={values.tilvisun}
                            />
                        </div>
                        <div className="form__col actions">
                            <div className="form__actions">
                                <Button
                                    style={{'margin': '0px'}}
                                    showLoader={loading}
                                    buttonType="buy"
                                    size="lg"
                                    disabled={(
                                        values.fastanr === ''
                                        && values.skraningarnr === ''
                                        && values.verksmidjunr === ''
                                    )}
                                >
                                    Kaupa
                                </Button>
                            </div>
                        </div>
                    </div>
                    {bondsAllowed &&
                        <div className="form__row">
                            <div className="form__col">
                                <Checkbox
                                    label="með veðböndum"
                                    name="bonds"
                                    id="bonds"
                                    value={values.bonds.toString()}
                                />
                            </div>
                        </div>
                    }
                </div>
            </div>
        </Form>
    );
}

